import { useCallback } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import {
  usePSD2OfflineSessionsInitMutation,
  usePSD2ProxySessionsInitMutation,
} from 'hooks/webapi';
import {
  MANUAL_PSD2_CONSENTS,
  MANUAL_PSD2_GUIDE,
  OFFLINE_PSD2_CONSENTS,
  OFFLINE_PSD2_GUIDE,
  PSD2_CONSENTS,
} from 'Router/routes';
import useError from 'hooks/useError';
import useNavigateSearch from 'hooks/useNavigateSearch';

export default function usePSD2Guide() {
  const location = useLocation();
  const params = useParams();
  const { showError } = useError();
  const isOffline = location.pathname.includes(OFFLINE_PSD2_GUIDE);
  const navigateSearch = useNavigateSearch();
  const [searchParams] = useSearchParams();

  const {
    mutateAsync: initializePSD2Session,
    isPending: initializePSD2SessionIsPending,
  } = usePSD2ProxySessionsInitMutation();
  const { mutateAsync: offlineInitializePSD2Session } =
    usePSD2OfflineSessionsInitMutation();

  const bankId = searchParams.get('bankId') || '';
  const bankName = searchParams.get('bankName') || '';
  const isManual = location.pathname === MANUAL_PSD2_GUIDE;

  const initPSD2Session = useCallback(async () => {
    if (isOffline) {
      if (!params.token) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }
      try {
        await offlineInitializePSD2Session(params.token);
      } catch (e) {
        if (e instanceof Error && e.message === '500') {
          showError(
            new Error(
              'Nepodařilo se inicializovat PSD2 session. Možná je token neplatný.',
            ),
          );
          return;
        }
        throw e;
      }
    } else {
      await initializePSD2Session({
        bankAccountChange: false,
        bankCode: bankId,
      });
    }

    if (isManual && !isOffline) {
      navigateSearch(MANUAL_PSD2_CONSENTS);
    } else {
      navigateSearch(
        isOffline ? `${OFFLINE_PSD2_CONSENTS}/${params.token}` : PSD2_CONSENTS,
      );
    }
  }, [bankId, bankName]);

  return {
    initPSD2Session,
    pending: initializePSD2SessionIsPending,
    bankName,
    isManual,
  };
}
